import { useCallback, useEffect, useState } from "react";
import { SeoOpportunityError, SeoOpportunityItem } from "../types/strategy";
import { isNotEmpty } from "../utils/type-utils";
import useStrategyApi from "../api/use-strategy-api";
import { BrandDetails } from "../types/add-brand-types";
import { useTimeUtil } from "./useTimeUtil";
import { trackEvent } from "../utils/firebase-analytics";
// import useCreatorStudioApi from "../api/use-creator-studio-api";
// import { ContentMode } from "../types/creator-studio";

const SEO_REFRESH_COOLDOWN_CODE = "SEO_REFRESH_COOLDOWN";

const isSeoOpportunityError = (
  response: unknown,
): response is SeoOpportunityError => {
  return (
    !!response &&
    typeof response === "object" &&
    "status" in response &&
    (response as SeoOpportunityError).status === "error" &&
    "error" in response
  );
};

export const useSeoAnalysisCard = (brandData: BrandDetails) => {
  const { generateSeoOpportunities: fetchSeoOpportunities } = useStrategyApi();
  const [seoOpportunities, setSeoOpportunities] = useState<
    SeoOpportunityItem[]
  >([]);
  const [loadingSeo, setLoadingSeo] = useState(false);
  const [lastAuditTime, setLastAuditTime] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);
  const [showSeoTooltip, setShowSeoTooltip] = useState(false);
  const [showRefreshTooltip, setShowRefreshTooltip] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const { formatDate } = useTimeUtil();

  const loadingMessages = [
    "Analyzing search optimization opportunities...",
    "Identifying strategic priorities...",
    "Evaluating market trends and insights...",
    "Discovering high-impact keywords...",
  ];

  const canRefresh = useCallback(() => {
    if (!lastAuditTime) return true;

    const lastAudit = new Date(lastAuditTime);
    const now = new Date();
    const daysDifference =
      (now.getTime() - lastAudit.getTime()) / (1000 * 60 * 60 * 24);

    return daysDifference >= 5;
  }, [lastAuditTime]);

  const getDaysUntilNextRefresh = useCallback(() => {
    if (!lastAuditTime) return 0;

    const lastAudit = new Date(lastAuditTime);
    const now = new Date();
    const daysDifference =
      (now.getTime() - lastAudit.getTime()) / (1000 * 60 * 60 * 24);

    return Math.max(0, Math.ceil(5 - daysDifference));
  }, [lastAuditTime]);

  const getCooldownErrorMessage = useCallback((error: SeoOpportunityError) => {
    const daysRemaining = error.error.days_remaining;
    if (typeof daysRemaining === "number") {
      return `You can refresh SEO opportunities in ${daysRemaining} day${
        daysRemaining === 1 ? "" : "s"
      }. Please wait.`;
    }

    if (error.message) return error.message;

    return "SEO opportunities are still in cooldown. Please try again later.";
  }, []);

  const getSEO = useCallback(
    async (refresh: boolean) => {
      setLoadingSeo(true);
      const response = await fetchSeoOpportunities(brandData.id, refresh);
      if (isSeoOpportunityError(response)) {
        if (refresh) {
          trackEvent("seo_opportunities_generation_failed", {
            reason: response.error.code || "api_error",
          });
        }
        if (
          refresh &&
          response.error.code?.toUpperCase() === SEO_REFRESH_COOLDOWN_CODE
        ) {
          setToast({
            message: getCooldownErrorMessage(response),
            type: "warning",
            isVisible: true,
          });
        } else if (refresh) {
          setToast({
            message:
              response.message || "Failed to generate SEO opportunities!",
            type: "error",
            isVisible: true,
          });
        }
      } else if (isNotEmpty(response)) {
        // Extract opportunities and last_audit from API response
        setSeoOpportunities(response.data.opportunities || []);
        if (refresh) {
          trackEvent("seo_opportunities_generated", {
            item_count: response.data.opportunities?.length ?? 0,
          });
        }

        // Use last_audit from server response
        if (response.data.last_audit) {
          setLastAuditTime(response.data.last_audit);
        }

        if (refresh) {
          setToast({
            message: "SEO generated successfully!",
            type: "success",
            isVisible: true,
          });
        }
      } else if (refresh) {
        trackEvent("seo_opportunities_generation_failed", {
          reason: "empty_response",
        });
        setToast({
          message: "Failed to generate SEO opportunities!",
          type: "error",
          isVisible: true,
        });
      }
      setLoadingSeo(false);
    },
    [brandData.id, fetchSeoOpportunities, getCooldownErrorMessage],
  );

  const generateSeoOpportunities = useCallback(
    async (refresh: boolean = false) => {
      // Check cooldown period for refresh
      if (refresh && !canRefresh()) {
        const daysRemaining = getDaysUntilNextRefresh();
        setToast({
          message: `You can refresh SEO opportunities in ${daysRemaining} day${
            daysRemaining > 1 ? "s" : ""
          }. Please wait.`,
          type: "warning",
          isVisible: true,
        });
        setTimeout(() => setToast(null), 4000);
        return;
      }

      if (refresh) {
        trackEvent("seo_opportunities_generation_started");
      }
      await getSEO(refresh);
    },
    [canRefresh, getSEO, getDaysUntilNextRefresh],
  );

  useEffect(() => {
    getSEO(false);
  }, [getSEO]);

  useEffect(() => {
    if (loadingSeo) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 4000); // Change message every 4 seconds

      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0);
    }
  }, [loadingSeo, loadingMessages.length]);

  return {
    seoOpportunities,
    loadingSeo,
    generateSeoOpportunities,
    lastAuditTime,
    canRefresh,
    getDaysUntilNextRefresh,
    toast,
    showSeoTooltip,
    setShowSeoTooltip,
    showRefreshTooltip,
    setShowRefreshTooltip,
    loadingMessageIndex,
    setLoadingMessageIndex,
    formatDate,
    loadingMessages,
  };
};
